import { Box, Flex, HStack, Text, VStack } from '@chakra-ui/react'
import { useMemo } from 'react'
import {
  Cell,
  Pie,
  PieChart as RechartsPieChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts'

import { useIsDark } from '../../../hooks/useIsDark'
import { FloatingChartTooltipShell } from '../chartTooltip/ChartTooltipPointerContext'
import { FloatingPieChartTooltip } from '../chartTooltip/FloatingPieChartTooltip'
import '../common/Chart.css'
import {
  PIE_CHART_DIAMETER,
  PIE_CHART_END_ANGLE,
  PIE_CHART_LABEL_MARGIN,
  PIE_CHART_LEGEND_DOT_BORDER,
  PIE_CHART_LEGEND_DOT_SHADOW,
  PIE_CHART_LEGEND_DOT_SIZE,
  PIE_CHART_MAX_LEGEND_GAP,
  PIE_CHART_MIN_LABEL_PCT,
  PIE_CHART_MIN_LEGEND_GAP,
  PIE_CHART_PERCENT_LABEL_OFFSET,
  PIE_CHART_START_ANGLE,
  PIE_OTHER_SLICE_COLOR_DARK,
  PIE_OTHER_SLICE_COLOR_LIGHT,
  PIE_OTHER_THRESHOLD_PCT,
  getPieChartColor,
} from '../common/chart.constants'
import { ChartData } from '../common/chart.types'
import { formatChartDataLabelValue } from '../common/chartFormat'
import { useChartColors } from '../common/useChartColors'

export interface PieChartProps {
  chartData: ChartData
  /** Override chart height (px). */
  chartHeight?: number
  /** Minimum percent for a slice to display individually. Below this they become "Other". */
  otherThresholdPct?: number
  /** Label under the center total (e.g. cube context). Default matches Port-to-Port "Container Count". */
  centerTotalCaption?: string
  /** Row label for the slice value in the floating tooltip (e.g. "Container Count"). */
  tooltipValueLabel?: string
}

const RADIAN = Math.PI / 180
/** Donut geometry — keep in sync with `<Pie innerRadius` / `outerRadius` below. */
const PIE_INNER_RADIUS = 90
const PIE_OUTER_RADIUS = 130
/**
 * Width of the SVG column only (not the legend). Sized to the donut + outside % labels
 * so Recharts does not clip labels and the legend stays beside the plot (Figma).
 */
const PIE_PLOT_WIDTH_PX =
  (PIE_OUTER_RADIUS + PIE_CHART_PERCENT_LABEL_OFFSET + PIE_CHART_LABEL_MARGIN) *
  2
const DEFAULT_VALUE_KEY = 'value'
const DEFAULT_NAME_KEY = 'key'

export const PieChart = ({
  chartData,
  chartHeight = PIE_CHART_DIAMETER,
  otherThresholdPct = PIE_OTHER_THRESHOLD_PCT,
  centerTotalCaption = 'Total Containers',
  tooltipValueLabel,
  ...props
}: PieChartProps) => {
  const chartColors = useChartColors()
  const isDark = useIsDark()
  const valueKey = chartData.valueKey ?? DEFAULT_VALUE_KEY
  const nameKey = chartData.nameKey ?? DEFAULT_NAME_KEY
  const values = chartData.chartValues ?? []

  const total = useMemo(
    () =>
      values.reduce((sum, entry) => {
        const val = entry[valueKey]
        return sum + (Number(val) || 0)
      }, 0),
    [values, valueKey],
  )

  const { visible, other } = useMemo(() => {
    const vis: typeof values = []
    let otherTotal = 0
    const otherItems: { name: string; value: number }[] = []

    const appendOther = (entry: (typeof values)[number]) => {
      const val = Number(entry[valueKey]) || 0
      otherTotal += val
      otherItems.push({ name: String(entry[nameKey] ?? ''), value: val })
    }

    const dataTopN =
      chartData.dataTopN == null
        ? null
        : Math.max(0, Math.floor(chartData.dataTopN))

    if (dataTopN != null) {
      const sorted = [...values].sort(
        (left, right) =>
          (Number(right[valueKey]) || 0) - (Number(left[valueKey]) || 0),
      )
      if (sorted.length <= dataTopN) {
        return { visible: sorted, other: null }
      }

      vis.push(...sorted.slice(0, dataTopN))
      sorted.slice(dataTopN).forEach(appendOther)
    } else {
      for (const entry of values) {
        const val = Number(entry[valueKey]) || 0
        const pct = total > 0 ? (val / total) * 100 : 0
        if (pct >= otherThresholdPct) {
          vis.push(entry)
        } else {
          appendOther(entry)
        }
      }
    }

    otherItems.sort((left, right) => right.value - left.value)
    return {
      visible: vis,
      other:
        otherTotal > 0
          ? {
              [nameKey]: 'Other',
              [valueKey]: otherTotal,
              _otherItems: otherItems,
              _isOther: true,
            }
          : null,
    }
  }, [values, valueKey, nameKey, total, otherThresholdPct, chartData.dataTopN])

  const chartSlices = useMemo(
    () => (other ? [...visible, other] : visible),
    [visible, other],
  )

  let rankColorIndex = 0
  const dataWithColors = chartSlices.map(entry => {
    const isOtherSlice = Boolean((entry as { _isOther?: boolean })._isOther)
    return {
      ...entry,
      fill: isOtherSlice
        ? isDark
          ? PIE_OTHER_SLICE_COLOR_DARK
          : PIE_OTHER_SLICE_COLOR_LIGHT
        : getPieChartColor(rankColorIndex++, isDark),
    }
  })

  const renderPercentLabel = (labelProps: {
    cx: number
    cy: number
    midAngle: number
    outerRadius: number
    percent?: number
    value?: number
  }) => {
    const { cx, cy, midAngle, outerRadius, percent, value } = labelProps
    const pct =
      percent != null
        ? Math.round(percent * 100)
        : total > 0
          ? Math.round(((value ?? 0) / total) * 100)
          : 0
    if (pct < PIE_CHART_MIN_LABEL_PCT) return null
    const radius = outerRadius + PIE_CHART_PERCENT_LABEL_OFFSET
    const x = cx + radius * Math.cos(-midAngle * RADIAN)
    const y = cy + radius * Math.sin(-midAngle * RADIAN)
    return (
      <text
        x={x}
        y={y}
        textAnchor="middle"
        dominantBaseline="central"
        className="pie-chart-percent-label"
        fill={chartColors.piePercentLabelFill}
      >
        {pct}%
      </text>
    )
  }

  const FloatingPieTooltipWrapper = (tooltipProps: any) => (
    <FloatingPieChartTooltip
      {...tooltipProps}
      total={total}
      nameKey={nameKey}
      valueFormat={chartData.valueFormat}
      valueLabel={tooltipValueLabel}
    />
  )

  const pieChartInner = (
    <Box position="relative" width="100%" height="100%">
      <ResponsiveContainer width="100%" height="100%">
        <RechartsPieChart
          margin={{
            top: PIE_CHART_LABEL_MARGIN,
            right: PIE_CHART_LABEL_MARGIN,
            bottom: PIE_CHART_LABEL_MARGIN,
            left: PIE_CHART_LABEL_MARGIN,
          }}
        >
          <Pie
            data={dataWithColors}
            dataKey={valueKey}
            nameKey={nameKey}
            cx="50%"
            cy="50%"
            innerRadius={PIE_INNER_RADIUS}
            outerRadius={PIE_OUTER_RADIUS}
            startAngle={PIE_CHART_START_ANGLE}
            endAngle={PIE_CHART_END_ANGLE}
            stroke={chartColors.pieSliceStroke}
            strokeWidth={2}
            isAnimationActive={false}
            label={renderPercentLabel}
            labelLine={false}
          >
            {dataWithColors.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.fill} />
            ))}
          </Pie>
          <Tooltip content={<FloatingPieTooltipWrapper />} />
        </RechartsPieChart>
      </ResponsiveContainer>
      <VStack
        position="absolute"
        top="50%"
        left="50%"
        transform="translate(-50%, -50%)"
        spacing="3px"
        pointerEvents="none"
      >
        <Text
          color={chartColors.pieCenterTotalFill}
          fontSize="28px"
          fontWeight="600"
          lineHeight="1"
        >
          {formatChartDataLabelValue(total, chartData.valueFormat)}
        </Text>
        <Text
          color={chartColors.pieCenterCaptionFill}
          fontSize="12px"
          fontWeight="500"
          lineHeight="1"
        >
          {centerTotalCaption}
        </Text>
      </VStack>
    </Box>
  )

  return (
    <Box {...props}>
      <Flex
        alignItems="center"
        justifyContent="flex-start"
        flexWrap="wrap"
        columnGap={`clamp(${PIE_CHART_MIN_LEGEND_GAP}px, 4vw, ${PIE_CHART_MAX_LEGEND_GAP}px)`}
        rowGap="24px"
      >
        <FloatingChartTooltipShell>
          {mouseHandlers => (
            <Box
              flexShrink={0}
              w={`${PIE_PLOT_WIDTH_PX}px`}
              maxW="100%"
              height={chartHeight}
              className="chart-recharts-root chart-pie-chart"
              data-color-mode={isDark ? 'dark' : 'light'}
              {...mouseHandlers}
            >
              {pieChartInner}
            </Box>
          )}
        </FloatingChartTooltipShell>

        <Box
          maxHeight={chartHeight}
          overflowY="auto"
          flexShrink={0}
          minW="min-content"
          pr="4px"
        >
          <VStack alignItems="flex-start" spacing="11px">
            {dataWithColors.map((entry, index) => {
              const rawName = entry[nameKey]
              const name = String(rawName ?? '')
              return (
                <HStack key={index} spacing="8px" alignItems="center">
                  <Box
                    w={PIE_CHART_LEGEND_DOT_SIZE}
                    h={PIE_CHART_LEGEND_DOT_SIZE}
                    borderRadius="full"
                    bg={entry.fill}
                    border={PIE_CHART_LEGEND_DOT_BORDER}
                    boxShadow={PIE_CHART_LEGEND_DOT_SHADOW}
                    flexShrink={0}
                    data-testid="pie-chart-legend-dot"
                  />
                  <Text
                    color={chartColors.pieLegendLabelColor}
                    fontSize="12px"
                    fontWeight="500"
                  >
                    {name}
                  </Text>
                </HStack>
              )
            })}
          </VStack>
        </Box>
      </Flex>
    </Box>
  )
}
